import { INews } from '@/@types/News'
import { useAuth } from '@/auth/Auth'
import { usePermissions } from '@/auth/PermissionsContext'
import { AppSidebar } from '@/components/app-sidebar'
import Loader from '@/components/Loader/Loader'
import { DocumentUpdateModal } from '@/components/Onboarding/DocumentUpdateModal'
import HeaderOnboard from '@/components/Onboarding/HeaderOnboard'
import { NewsModal } from '@/components/Onboarding/NewsModal'
import { WelcomeModal } from '@/components/Onboarding/WelcomeModal'
import CustomBreadcrumb, {
  BreadcrumbItemProps,
} from '@/components/shared/CustomBreadcrumb/CustomBreadcrumb'
import CustomTrigger from '@/components/shared/CustomTrigger/CustomTrigger'

import { GenericModal } from '@/components/Onboarding/GenericModal'

import { CurrencyToggle } from '@/components/ui/currency-toggle'
import { LanguageDropdown } from '@/components/ui/language-dropdown'
import { Separator } from '@/components/ui/separator'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { UserDropdown } from '@/components/UserDropdown/UserDropdown'

import { cn } from '@/lib/utils'
import { AuthService } from '@/services/auth.service'
import { NewsService } from '@/services/news.service'
import { OnboardingService } from '@/services/onboarding.service'
import useModalStore from '@/store/useModalStore'
import { useChangeLanguage } from '@/hooks/useChangeLanguage'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { isOnboardingIdentityVerificationRequired } from '@/utils/onboardingIdentityVerification'
import { ProductsService } from '@/services/products.service'
import { ProductRole } from '@/@types/Products'
import { getContractRedirectTarget } from '@/auth/contractRedirect'

interface LayoutInsideprops {
  children: React.ReactNode
  items: BreadcrumbItemProps[]
  isFinishedOnboarding?: boolean
  hasBgBlur?: boolean
  fullWidth?: boolean
}

export default function LayoutInside({
  children,
  items,
  hasBgBlur = false,
  fullWidth = false,
}: LayoutInsideprops) {
  const { authData } = useAuth()
  const { isPrimary } = usePermissions()
  const { t } = useTranslation(['common', 'onboarding', 'sales', 'errors'])
  const navigate = useNavigate()
  const location = useLocation()
  const { openModal, modals } = useModalStore()
  const identityVerificationRequired = isOnboardingIdentityVerificationRequired()

  const { data: userInfo, isPending: isLoadingUserInfo } = useQuery({
    queryKey: ['user-info'],
    queryFn: async () => await AuthService.userInfo(),
    staleTime: 1000 * 60 * 60 * 24,
  })

  const { data: bank, isLoading } = useQuery({
    queryKey: ['list-banks', authData?.id],
    queryFn: async () => {
      const response = await OnboardingService.getBanks()
      return response?.data?.[0]
    },
    enabled: Boolean(authData?.id) && isPrimary,
  })

  const routeTo =
    userInfo?.onboarding_step === 'finished' ? '/my-account' : '/onboarding'

  const [showWelcomeModal, setShowWelcomeModal] = useState(false)
  const [showDocumentUpdateModal, setShowDocumentUpdateModal] = useState(false)
  const [showNewsModal, setShowNewsModal] = useState(false)
  const [currentNews, setCurrentNews] = useState<INews | null>(null)
  const [newsIndex, setNewsIndex] = useState(0)
  const [hasShownRejectionPopup, setHasShownRejectionPopup] = useState(false)
  const [cookies, setCookie] = useCookies([
    'modalClosedTime',
    'documentUpdateModalClosedTime',
    'newsModalClosedTime',
  ])

  const { data: activeNews = [] } = useQuery({
    queryKey: ['active-news'],
    queryFn: NewsService.getActiveNews,
    staleTime: 1000 * 60 * 60 * 4, // 4 hours
  })

  useEffect(() => {
    // Após onboarding concluído e documentação aprovada: exige assinatura do contrato.
    if (isLoadingUserInfo) return
    if (location.pathname === '/contract') return

    const to = getContractRedirectTarget(location, userInfo)
    if (!to) return

    navigate(to, { replace: true })
  }, [
    isLoadingUserInfo,
    navigate,
    location.hash,
    location.pathname,
    location.search,
    userInfo?.contract_signed,
    userInfo?.documentation_status,
    userInfo?.onboarding_step,
  ])

  const shouldShowModal = (): boolean => {
    const closedTime = cookies.modalClosedTime
    if (!closedTime) {
      return true
    }
    const currentTime = Date.now()
    return currentTime - closedTime >= 5 * 60 * 1000 // 5 minutos
  }

  const shouldShowDocumentFinishedModal = (): boolean => {
    const closedTime = cookies.modalClosedTime
    if (!closedTime) {
      return true
    }
    const currentTime = Date.now()
    return currentTime - parseInt(closedTime, 10) >= 5 * 60 * 1000 // 5 minutos
  }

  const shouldShowDocumentUpdateModal = (): boolean => {
    const closedTime = cookies.documentUpdateModalClosedTime
    if (!closedTime) {
      return true
    }
    const currentTime = Date.now()
    return currentTime - parseInt(closedTime, 10) >= 5 * 60 * 1000 // 5 minutos
  }

  const shouldShowNewsModal = (newsId: number): boolean => {
    const closedNewsIds = cookies.newsModalClosedTime
    if (!closedNewsIds) {
      return true
    }
    const closedNews = closedNewsIds[newsId]
    if (!closedNews) {
      return true
    }
    const currentTime = Date.now()
    return currentTime - parseInt(closedNews, 10) >= 24 * 60 * 60 * 1000 // 24 hours
  }

  useEffect(() => {
    const baseStepsCompleted = !!(
      userInfo?.onboarding_detail?.profile &&
      userInfo?.onboarding_detail?.address &&
      userInfo?.onboarding_detail?.bankAccount &&
      userInfo?.onboarding_detail?.document
    )

    const isEffectivelyFinishedOnboarding =
      userInfo?.onboarding_step === 'finished' ||
      (!identityVerificationRequired &&
        userInfo?.onboarding_step === 'identity_verification') ||
      (!identityVerificationRequired && baseStepsCompleted)

    if (!isEffectivelyFinishedOnboarding) {
      if (location.pathname !== '/onboarding') {
        if (shouldShowModal()) {
          setShowWelcomeModal(true)
        }
      }
    }

    if (userInfo?.documentation_status === 'approved') {
      if (shouldShowDocumentFinishedModal()) {
        openModal('user-onboarding-completed')
      }
    }

    if (userInfo?.documentation_status === 'disapproved') {
      if (
        location.pathname !== '/my-account' &&
        shouldShowDocumentUpdateModal()
      ) {
        setShowDocumentUpdateModal(true)
      }
    }
  }, [
    location.pathname,
    userInfo?.documentation_status,
    userInfo?.onboarding_step,
    userInfo?.onboarding_detail?.profile,
    userInfo?.onboarding_detail?.address,
    userInfo?.onboarding_detail?.bankAccount,
    userInfo?.onboarding_detail?.document,
    identityVerificationRequired,
  ])

  useEffect(() => {
    if (location.pathname !== '/my-account' || !userInfo?.onboarding_detail) {
      return
    }

    // Onboarding já finalizado (ex.: perfil reprovado): correção em Minha Conta.
    if (userInfo.onboarding_step === 'finished') {
      return
    }

    const hasProfile = Boolean(userInfo.onboarding_detail.profile)
    if (!hasProfile) {
      navigate('/onboarding', { replace: true })
    }
  }, [
    location.pathname,
    navigate,
    userInfo?.onboarding_detail,
    userInfo?.onboarding_detail?.profile,
    userInfo?.onboarding_step,
  ])

  useEffect(() => {
    if (userInfo?.onboarding_rejection_reason && !hasShownRejectionPopup) {
      openModal('user-onboarding-rejected')
      setHasShownRejectionPopup(true)
    }
  }, [hasShownRejectionPopup, openModal, userInfo?.onboarding_rejection_reason])

  useEffect(() => {
    const areOtherModalsOpen =
      showWelcomeModal ||
      showDocumentUpdateModal ||
      modals['user-onboarding-completed']

    if (activeNews.length > 0 && !showNewsModal && !areOtherModalsOpen) {
      for (let i = newsIndex; i < activeNews.length; i++) {
        if (shouldShowNewsModal(activeNews[i].id)) {
          setCurrentNews(activeNews[i])
          setNewsIndex(i)
          setShowNewsModal(true)
          break
        }
      }
    }
  }, [
    activeNews,
    showWelcomeModal,
    showDocumentUpdateModal,
    modals,
    showNewsModal,
    newsIndex,
  ])

  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false)
    setCookie('modalClosedTime', Date.now(), { path: '/', maxAge: 300 })
  }

  const handleCloseDocumentModal = () => {
    setShowWelcomeModal(false)
    setCookie('modalClosedTime', Date.now(), { path: '/', maxAge: 300 })
  }

  const handleFinishOnboarding = () => {
    setShowWelcomeModal(false)
    navigate('/onboarding')
  }

  const handleCloseDocumentUpdateModal = () => {
    setShowDocumentUpdateModal(false)
    setCookie('documentUpdateModalClosedTime', Date.now(), {
      path: '/',
      maxAge: 300,
    })
  }

  const handleUpdateDocuments = () => {
    setShowDocumentUpdateModal(false)
    setCookie('documentUpdateModalClosedTime', Date.now(), {
      path: '/',
      maxAge: 300,
    })
    navigate('/my-account')
  }

  const handleCloseNewsModal = () => {
    if (currentNews) {
      const currentClosedNews = cookies.newsModalClosedTime || {}
      setCookie(
        'newsModalClosedTime',
        { ...currentClosedNews, [currentNews.id]: Date.now() },
        { path: '/', maxAge: 86400 }, // 24 hours
      )
    }
    setShowNewsModal(false)
    setCurrentNews(null)

    setNewsIndex((prev) => prev + 1)
  }

  const { i18n } = useTranslation()
  const handleChangeLanguage = useChangeLanguage()

  const handleGoToFirstProducerProductUrls = () => {

    if (userInfo?.first_product_url) {
      navigate(`/products/edit-product/${userInfo?.first_product_url}/geral`)
      return
    }

    navigate('/products')
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className={cn(hasBgBlur ? 'bg-blur' : '')}>
        <header className="flex bg-black lg:bg-black px-5 sm:px-6 max-lg:pl-[max(1.25rem,env(safe-area-inset-left))] max-lg:pr-[max(1.25rem,env(safe-area-inset-right))] justify-between h-16 shrink-0 border-b border-b-colorBorder items-center gap-3 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 min-w-0">
            <SidebarTrigger className="-ml-1 hidden lg:block" />
            <Separator
              orientation="vertical"
              className="mr-2 h-4 hidden lg:block"
            />
            <CustomTrigger />
            <div className="hidden lg:block">
              <CustomBreadcrumb items={items} />
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {userInfo?.multi_wallet_enabled && <CurrencyToggle />}
            <LanguageDropdown
              defaultValue={i18n.language || 'pt'}
              onChange={handleChangeLanguage}
              slim
            />
            <UserDropdown profilePhotoUrl={userInfo?.profile_photo?.url || ''} />
          </div>
        </header>


        {isLoadingUserInfo ? (
          <div className="flex items-center justify-center h-[calc(100vh-120px)]">
            <Loader />
          </div>
        ) : (
          <>
            <section className="lg:hidden bg-[#18181b] lg:bg-bgDark px-5 sm:px-6 max-lg:pl-[max(1.25rem,env(safe-area-inset-left))] max-lg:pr-[max(1.25rem,env(safe-area-inset-right))] py-2 border-b border-b-colorBorder">
              <CustomBreadcrumb items={items} />
            </section>
            {(userInfo?.read_only || authData?.read_only) && (
              <HeaderOnboard
                icon={<AlertCircle />}
                text={t('errors:store_read_only.description')}
                variant="destructive"
              />
            )}
            {userInfo?.documentation_status !== 'analyzing' &&
              userInfo?.documentation_status !== 'approved' && (
                <HeaderOnboard
                  icon={<AlertCircle />}
                  text={t('onboarding:finish_to_start')}
                  buttonText={t('onboarding:take_me')}
                  onClick={() => navigate('/onboarding')}
                />
              )}
            {userInfo?.documentation_status === 'analyzing' && (
              <HeaderOnboard
                icon={<AlertCircle />}
                text={t('onboarding:documents_pending')}
              />
            )}
            {/* Verificação de adquirente pendente. Vive aqui, e não só na página de onboarding,
                porque um vendedor que já concluiu o cadastro não volta lá por conta própria —
                sem este aviso ele nunca descobriria que há algo a fazer. */}
            {userInfo?.has_pending_acquirer_onboarding && (
              <HeaderOnboard
                icon={<AlertCircle />}
                text="Falta pouco para ativar sua nova conta de recebimento."
                buttonText={t('onboarding:take_me')}
                onClick={() => navigate('/onboarding')}
              />
            )}
            {userInfo?.onboarding_rejection_reason && (
              <HeaderOnboard
                icon={<AlertCircle />}
                text={
                  <>
                    <span className="block font-semibold">
                      ⚠️ {t('common:attention')}
                    </span>
                    <span className="block font-medium">
                      {userInfo.onboarding_rejection_reason}
                    </span>
                  </>
                }
                variant="destructive"
              />
            )}

            {userInfo?.url_rejected_reason && (
              <HeaderOnboard
                icon={<AlertCircle />}
                text={
                  <>
                    <span className="block font-semibold">
                      ⚠️ {t('common:attention')}
                    </span>
                    <span className="block font-medium">
                      {userInfo.url_rejected_reason}
                    </span>
                  </>
                }
                buttonText={t('onboarding:take_me')}
                onClick={handleGoToFirstProducerProductUrls}
                variant="destructive"
              />
            )}

            {userInfo?.onboarding_rejection_reason && (
              <GenericModal
                modalKey="user-onboarding-rejected"
                title={`⚠️ ${t('common:attention')}`}
                description={
                  <div className="space-y-1">
                    <p className="font-medium text-foreground break-words">
                      {userInfo.onboarding_rejection_reason}
                    </p>
                  </div>
                }
                cancelButtonText={t('common:export_modal.close')}
                confirmButtonText={t('sales:understood')}
                icon={null as any}
                hideImage
                hideCancelButton
              />
            )}

            {userInfo?.kyc_status === null &&
              userInfo?.documentation_status !== 'pending' &&
              !userInfo?.has_acquirer && (
                <HeaderOnboard
                  icon={<AlertCircle />}
                  text={t('onboarding:kyc_not_started')}
                  variant="destructive"
                  buttonText={t('onboarding:take_me')}
                  onClick={() => navigate('/onboarding')}
                />
              )}

            {userInfo?.documentation_status !== 'approved' && (
              <GenericModal
                title={t('onboarding:documents_approved')}
                description={t('onboarding:documents_approved_description')}
                onCancel={handleCloseDocumentModal}
                onConfirm={handleCloseDocumentModal}
                confirmButtonText={t('onboarding:start_selling')}
                modalKey="user-onboarding-completed"
              />
            )}

            {userInfo?.documentation_status === 'disapproved' && (
              <HeaderOnboard
                icon={<AlertCircle />}
                title={t('onboarding:update_title')}
                text={t('onboarding:documents_rejected')}
                buttonText={t('onboarding:take_me')}
                onClick={() => navigate('/my-account')}
                variant="destructive"
              />
            )}

            {userInfo?.kyc_status === 'declined' && (
              <HeaderOnboard
                icon={<AlertCircle />}
                text={
                  userInfo?.kyc_veriff_reason ??
                  t('onboarding:documents_rejected')
                }
                buttonText={userInfo?.kyc_veriff_reason}
                onClick={() => navigate('/my-account')}
                variant="destructive"
              />
            )}

            {isPrimary && bank?.agency_digit === '11' && (
              <HeaderOnboard
                icon={<AlertCircle />}
                text={t('onboarding:update_digits')}
                buttonText={t('onboarding:take_me')}
                onClick={() => {
                  navigate('/my-account')
                  openModal('bank')
                }}
                variant="default"
              />
            )}

            {showWelcomeModal && (
              <WelcomeModal
                isOpen={showWelcomeModal}
                onClose={handleCloseWelcomeModal}
                onFinishOnboarding={handleFinishOnboarding}
                showDashboardButton={location.pathname !== '/dashboard'}
              />
            )}

            {showDocumentUpdateModal && (
              <DocumentUpdateModal
                isOpen={showDocumentUpdateModal}
                onClose={handleCloseDocumentUpdateModal}
                onUpdateDocuments={handleUpdateDocuments}
              />
            )}

            <main
              className={
                fullWidth
                  ? 'w-full'
                  : 'w-full mx-auto px-5 sm:px-6 pt-4 pb-6 max-lg:pl-[max(1.25rem,env(safe-area-inset-left))] max-lg:pr-[max(1.25rem,env(safe-area-inset-right))] md:p-6 xl:max-w-[1200px] 2xl:max-w-[1440px] lg:px-[5%]'
              }
            >
              {children}
            </main>
          </>
        )}
      </SidebarInset>
    </SidebarProvider>
  )
}